import React, { useState, useEffect, useCallback } from 'react';
import PageContainer from '../components/layout/PageContainer';
import PageHeader from '../components/layout/PageHeader';
import { ServiceCard } from '../components';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { isStandardApiResponse } from '../types/api';
import bg001 from '../assets/images/bg-001.jpg';
import bg002 from '../assets/images/bg-002.jpg';
import bg003 from '../assets/images/bg-003.jpg';

interface SubscriptionPlan {
  id: number;
  name: string;
  display_name: string;
  description: string;
  price_monthly: number;
  formatted_price: string;
  max_storage_gb: number;
  max_projects: number | null;
  max_data_per_project: number | null;
  is_unlimited_projects: boolean;
  is_unlimited_data: boolean;
}

/**
 * その他ページコンポーネント
 * サービス一覧を表示
 */
const OthersPage: React.FC = () => {
  const { execute: apiCall } = useApi();
  const { isAuthenticated } = useAuth();
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);

  // プラン情報を取得
  const fetchPlans = useCallback(async () => {
    try {
      setPlansLoading(true);
      const response = await apiCall('/api/subscriptions/plans/');

      if (isStandardApiResponse(response) && response.status === 'success') {
        const plansData = response.data as { results: SubscriptionPlan[] };
        setSubscriptionPlans(plansData.results);

      } else if (response && typeof response === 'object' && 'results' in response) {
        // Legacy format fallback
        const plansData = response as { results: SubscriptionPlan[] };
        setSubscriptionPlans(plansData.results);

      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('プラン情報の取得に失敗しました:', error);
      }
    } finally {
      setPlansLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // プラン名でプランを検索
  const getPlanByName = (name: string) => {
    return subscriptionPlans.find(plan => plan.name === name);
  };

  // 通常サービス（ログイン不要・公開サービス）
  const publicServices = [
    {
      id: 1,
      title: 'Studio RiverSide',
      description: '台東区浅草橋のシェアワークスペース。Podcast収録もできます。',
      appLink: '#auth-service',
      image: bg003,
      icon: 'fas fa-shield-alt'
    },
  ];

  // プレミアムサービス（ログイン必要・無料プランデフォルト）
  const premiumServices = [
    {
      id: 4,
      title: '3D Analyzer',
      description: '3Dスキャンデータを解析し、デジタル計測を支援します。',
      appLink: `${process.env.REACT_APP_3D_ANALYZER_URL || 'http://localhost:3001'}`,
      image: bg001,
      icon: 'fas fa-cube'
    },
    {
      id: 5,
      title: 'Panel Layout',
      description: 'DXFデータで指定した領域に、最適なパネル配置を提案します。',
      appLink: '#panel-layout',
      image: bg002,
      icon: 'fas fa-th-large'
    }
  ];

  // プラン情報を取得
  const litePlan = getPlanByName('lite');
  const studioPlan = getPlanByName('studio');



  return (
    <PageContainer>
      <PageHeader title="その他のサービス" />
      
      {/* 通常サービス（タイトルなし） */}
      <div className="mb-5">
        <div className="row g-4">
          {publicServices.map(service => (
            <ServiceCard 
              key={service.id} 
              service={service}
            />
          ))}
        </div>
      </div>

      {/* プレミアムサービス（Premiumタイトル付き） */}
      <div className="mb-5">
        <div className="mb-4">
          <div className="d-flex align-items-center mb-2">
            <h4 className="mb-0">Premium</h4>
          </div>
          <small className="text-muted">
            プレミアムサービスをご利用いただくにはLITEプラン（無料）でのログインが必要です
          </small>
        </div>
        <div className="row g-4">
          {premiumServices.map(service => (
            <ServiceCard 
              key={service.id} 
              service={service}
              disabled={true} // 表示専用（リンク機能無効）
            />
          ))}
        </div>
      </div>

      {/* プラン情報セクション（動的表示） */}
      {!plansLoading && subscriptionPlans.length > 0 && (
        <div className="mb-5">
          <div className="row">
            {/* LITEプラン */}
            {litePlan && (
              <div className="col-md-6 mb-4">
                <div className="card border-0 shadow-sm h-100 border-success">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                      <h5 className="card-title mb-0 text-success">
                        <i className="fas fa-leaf me-2"></i>
                        {litePlan.display_name}
                      </h5>
                      <div className="ms-auto">
                        <h4 className="mb-0 text-success">
                          {litePlan.price_monthly === 0 ? '無料' : `¥${litePlan.price_monthly.toLocaleString()}/月`}
                        </h4>
                      </div>
                    </div>
                    <p className="card-text text-muted mb-3">
                      {litePlan.description}
                    </p>
                    <ul className="list-unstyled">
                      <li className="mb-2">
                        <i className="fas fa-hdd text-muted me-2"></i>
                        <strong>最大容量：</strong>{litePlan.max_storage_gb}GB（トータル）
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-folder text-muted me-2"></i>
                        <strong>登録プロジェクト最大数：</strong>
                        {litePlan.is_unlimited_projects ? '無制限' : `${litePlan.max_projects}個`}
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-database text-muted me-2"></i>
                        <strong>プロジェクトあたりの登録データ最大数：</strong>
                        {litePlan.is_unlimited_data ? '無制限' : `${litePlan.max_data_per_project}件`}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {/* STUDIOプラン */}
            {studioPlan && (
              <div className="col-md-6 mb-4">
                <div className="card border-0 shadow-sm h-100 border-primary">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                      <h5 className="card-title mb-0 text-primary">
                        <i className="fas fa-star me-2"></i>
                        {studioPlan.display_name}
                      </h5>
                      <div className="ms-auto">
                        <h4 className="mb-0 text-primary">
                          {studioPlan.price_monthly === 0 ? '無料' : `¥${studioPlan.price_monthly.toLocaleString()}/月`}
                        </h4>
                      </div>
                    </div>
                    <p className="card-text text-muted mb-3">
                      {studioPlan.description}
                    </p>
                    <ul className="list-unstyled">
                      <li className="mb-2">
                        <i className="fas fa-hdd text-muted me-2"></i>
                        <strong>最大容量：</strong>{studioPlan.max_storage_gb}GB（トータル）
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-folder text-muted me-2"></i>
                        <strong>登録プロジェクト最大数：</strong>
                        {studioPlan.is_unlimited_projects ? '無制限' : `${studioPlan.max_projects}個`}
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-database text-muted me-2"></i>
                        <strong>プロジェクトあたりの登録データ最大数：</strong>
                        {studioPlan.is_unlimited_data ? '無制限' : `${studioPlan.max_data_per_project}件`}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ログイン・新規登録セクション */}
      <div className="text-end mb-5">
        <button 
          className="btn btn-sm btn-primary px-3"
          data-bs-toggle="modal" 
          data-bs-target="#loginModal"
          disabled={isAuthenticated}
        >
          <i className={`fas ${isAuthenticated ? 'fa-check-circle' : 'fa-sign-in-alt'} me-2`}></i>
          {isAuthenticated ? 'ログイン済み' : 'ログイン'}
        </button>
      </div>
    </PageContainer>
  );
};

export default OthersPage; 